/**
 * Returns an object with text as an ellipsis of the list by elements that can fit within the given width, and isOverflow boolean true when list doesn't fit in given width
 * @param {String[]} list - The list of elements to contain within the width.
 * @param {Number} width - The width that the result should fit within.
 * @param {String} [delimiter = '>'] - The delimiter to separate the list elements with.
 * @param {Number} [fontSize = 14] - The font size used to calculate the ellipsis.
 * @param {Number} [fontWeight = 400] - The font weight used to calculate the ellipsis.
 * @param {String} [fontFamily = 'Artifakt Element'] - The font family used to calculate the ellipsis.
 * @returns {string}
 */
export const listTruncationWithOverflow = (
  list,
  width,
  delimiter = '>',
  fontSize = 14,
  fontWeight = 400,
  fontFamily = 'Artifakt Element'
) => {
  const context = document.createElement('canvas').getContext('2d');
  context.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  const ellipsis = '\u2026';

  // In case only one element exists
  if (list.length === 1) {
    // If it fits in given width - return it, and no overflow
    if (!width || context.measureText(list[0]).width <= width) {
      return { text: list[0], isOverflow: false };
    }
    // Else return text with isOverflow
    return { text: list[0], isOverflow: true };
  }
  const [first, ...middle] = list;
  const last = middle.pop();

  // In case width is not specified, or text can fit within the given width without truncation.
  let text = list.join(` ${delimiter} `).trim();
  if (!width || context.measureText(text).width <= width) {
    return { text, isOverflow: false };
  }

  // In case only the first and last elements can fit.
  text = [first, ellipsis, last].join(` ${delimiter} `).trim();
  if (context.measureText(text).width <= width) {
    return { text, isOverflow: true };
  }

  // If all else fails - return everything should be truncated except the last element.
  return { text: `${ellipsis} ${delimiter} ${last}`.trim(), isOverflow: true };
};

/**
 * Returns an ellipsis of the list by elements that can fit within the given width.
 * @param {String[]} list - The list of elements to contain within the width.
 * @param {Number} width - The width that the result should fit within.
 * @param {String} [delimiter = '>'] - The delimiter to separate the list elements with.
 * @param {Number} [fontSize = 14] - The font size used to calculate the ellipsis.
 * @param {Number} [fontWeight = 400] - The font weight used to calculate the ellipsis.
 * @param {String} [fontFamily = 'Artifakt Element'] - The font family used to calculate the ellipsis.
 * @returns {string}
 */
export default (
  list,
  width,
  delimiter = '>',
  fontSize = 14,
  fontWeight = 400,
  fontFamily = 'Artifakt Element'
) => listTruncationWithOverflow(list, width, delimiter, fontSize, fontWeight, fontFamily).text;
